import type { EventCheckinCodeResponse, ShareLinksResponse } from '@pulsify/shared/dto'
import * as m from '~/paraglide/messages'
import type { Event, Listing } from '~/shared/types'

export type FlyerFormat = 'a5' | 'a4' | 'white' | 'story'

/**
 * Ahead of the event with its date, on site at tables and the bar, for the queue outside, or a
 * promotion that leads to the event page to say Interessiert or plan it with friends.
 */
export type FlyerMotif = 'ahead' | 'onsite' | 'queue' | 'promo'

export type FlyerLocale = 'de' | 'en'

export const PAPER_SIZES_MM = {
  a5: { width: 148, height: 210 },
  a4: { width: 210, height: 297 },
  white: { width: 210, height: 297 },
} as const

export const WORDMARK_SRC = '/pulsify-wordmark.svg'
export const PRINT_LOGO_SRC = '/pulsify-wordmark.svg'

export interface FlyerContent {
  motif: FlyerMotif
  title: string
  when: string
  venue: string
  coverUrl: string | null
  code: string
  url: string
  headline: string
  tagline: string
  fineprint: string
  codeLabel: string
}

/** The share link meant for printed QR codes, so scans count toward the cashback. */
export function qrShareUrl(shareLinks: ShareLinksResponse | undefined): string | null {
  return shareLinks?.links.find((link) => link.channel === 'qr')?.url ?? null
}

export function buildFlyerContent(
  event: Event,
  checkin: EventCheckinCodeResponse,
  motif: FlyerMotif,
  locale: FlyerLocale,
  promoUrl: string | null = null,
): FlyerContent {
  const options = { locale }
  const start = new Date(event.startTime)
  const date = start.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
  const time = start.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  const place = [event.location.street, event.location.city].filter(Boolean).join(', ')
  const countOnly = event.liveRoomMode === 'count'
  const promo = motif === 'promo'
  const eventPage = promoUrl ?? `${new URL(checkin.url).origin}/events/${event.id}`

  return {
    motif,
    title: event.title,
    when: `${date} · ${time}`,
    venue: [event.location.name, place].filter(Boolean).join(' · '),
    coverUrl: event.coverImageUrl || event.images[0]?.url || null,
    code: checkin.code,
    url: promo ? eventPage : checkin.url,
    headline: promo
      ? m.promoFlyerHeadline({}, options)
      : countOnly
        ? m.checkinFlyerHeadlineCount({}, options)
        : motif === 'queue'
          ? m.checkinFlyerHeadlineQueue({}, options)
          : m.checkinFlyerHeadline({}, options),
    tagline: promo
      ? m.promoFlyerTagline({}, options)
      : countOnly
        ? m.checkinFlyerTaglineCount({}, options)
        : motif === 'queue'
          ? m.checkinFlyerTaglineQueue({}, options)
          : m.checkinFlyerTagline({}, options),
    fineprint: promo ? m.promoFlyerFineprint({}, options) : m.checkinFlyerFineprint({}, options),
    codeLabel: m.checkinFlyerCodeLabel({}, options),
  }
}

export function buildVenueFlyerContent(
  listing: Listing,
  venueCode: EventCheckinCodeResponse,
  motif: FlyerMotif,
  locale: FlyerLocale,
): FlyerContent {
  const options = { locale }
  return {
    motif,
    title: m.venueFlyerTitle({ venueName: listing.name }, options),
    when: m.venueFlyerWhen({}, options),
    venue: [listing.location.street, listing.location.city].filter(Boolean).join(', '),
    coverUrl: listing.coverImageUrl || listing.images[0]?.url || null,
    code: venueCode.code,
    url: venueCode.url,
    headline: m.venueFlyerHeadline({}, options),
    tagline: m.venueFlyerTagline({}, options),
    fineprint: m.checkinFlyerFineprint({}, options),
    codeLabel: m.checkinFlyerCodeLabel({}, options),
  }
}
